import { Cart } from '@diamond/shared/types';
import {
  isValidCartNote,
  NOTE_MAX_LENGTH_DEFAULT,
  NOTE_VALIDATION_ERROR_MESSAGE,
  useNoteInput,
  useUpdateCartNote,
} from '@diamond/sol-buyer/utils';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

export interface UseCartItemNoteArgs {
  item: Cart;
  isDisabled: boolean;
  /**
   * Optional getter to supply the latest magnified quantity when patching notes.
   * Use this to avoid overwriting quantity with a stale value because the backend PATCH
   * contract requires `{ quantity, note }` together.
   */
  getCurrentQuantityMagnified?: () => number;
  /**
   * Called when note validation fails (e.g. too short, hyphen-only, space-only). Use to show
   * error toast (e.g. "Catatan minimal 2 karakter").
   */
  onValidationError?: (message: string) => void;
}

export interface UseCartItemNoteReturn {
  isNoteFieldVisible: boolean;
  openNoteEditor: () => void;
  cancelNoteEdit: () => void;
  confirmNoteEdit: () => void;
  savedNote: string;
  currentNote: string;
  noteInputProps: ReturnType<typeof useNoteInput>['inputProps'];
  charCount: number;
  handleUpdateNote: (note: string) => void;
}

export const useCartItemNote = ({
  item,
  isDisabled,
  getCurrentQuantityMagnified,
  onValidationError,
}: UseCartItemNoteArgs): UseCartItemNoteReturn => {
  const [isNoteFieldVisible, setIsNoteFieldVisible] = useState(false);
  const [savedNote, setSavedNote] = useState<string>(item.note || '');

  const { handleUpdateNote } = useUpdateCartNote({
    itemId: item.id,
    getCurrentQuantityMagnified:
      getCurrentQuantityMagnified ?? (() => item.quantity),
  });

  const {
    currentNote,
    setCurrentNote,
    inputProps: baseNoteInputProps,
    charCount,
  } = useNoteInput({
    id: `note-${item.id}`,
    name: `note-${item.id}`,
    defaultValue: item.note || '',
    maxLength: NOTE_MAX_LENGTH_DEFAULT,
  });

  useEffect(() => {
    const nextSaved = item.note || '';
    setSavedNote(nextSaved);
    if (!isNoteFieldVisible) {
      setCurrentNote(nextSaved);
    }
  }, [item.note, item.id, isNoteFieldVisible, setCurrentNote]);

  const openNoteEditor = useCallback(() => {
    setCurrentNote(savedNote);
    setIsNoteFieldVisible(true);
  }, [savedNote, setCurrentNote]);

  const cancelNoteEdit = useCallback(() => {
    setCurrentNote(savedNote);
    setIsNoteFieldVisible(false);
  }, [savedNote, setCurrentNote]);

  const confirmNoteEdit = useCallback(() => {
    const trimmed = currentNote.trim();
    if (!isValidCartNote(trimmed)) {
      onValidationError?.(NOTE_VALIDATION_ERROR_MESSAGE);
      return;
    }
    setCurrentNote(trimmed);
    setSavedNote(trimmed);
    setIsNoteFieldVisible(false);
    handleUpdateNote(trimmed);
  }, [currentNote, handleUpdateNote, setCurrentNote, onValidationError]);

  // Enhanced inputProps with auto-save on blur
  const noteInputProps = useMemo(() => {
    const originalOnBlur = baseNoteInputProps.onBlur;
    return {
      ...baseNoteInputProps,
      onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
        originalOnBlur(e);
        const trimmed = e.currentTarget.value.trim();
        if (!isValidCartNote(trimmed)) {
          onValidationError?.(NOTE_VALIDATION_ERROR_MESSAGE);
          return;
        }
        setSavedNote(trimmed);
        setIsNoteFieldVisible(false);
        handleUpdateNote(trimmed);
      },
    };
  }, [baseNoteInputProps, handleUpdateNote, onValidationError]);

  const effectiveSavedNote = useMemo(() => savedNote, [savedNote]);

  return {
    isNoteFieldVisible,
    openNoteEditor,
    cancelNoteEdit,
    confirmNoteEdit,
    savedNote: effectiveSavedNote,
    currentNote,
    noteInputProps,
    charCount,
    handleUpdateNote,
  };
};
